export const OZONETEL_DEBUG_PREFIX = '[Ozonetel]';

export interface AgentAudioState {
  audioContext: AudioContext;
  destination: MediaStreamAudioDestinationNode;
  gainNode: GainNode;
  rawStream: MediaStream;
  sourceNode: MediaStreamAudioSourceNode;
  hasCapturedSdkStream: boolean;
  shouldInterceptNextAudioRequest: boolean;
}

export type NativeGetUserMedia = MediaDevices['getUserMedia'];

export interface GetUserMediaPatchState {
  hadOwnDescriptor: boolean;
  mediaDevices: MediaDevices;
  nativeGetUserMedia: NativeGetUserMedia;
  ownDescriptor: PropertyDescriptor | undefined;
  wrappedGetUserMedia: NativeGetUserMedia;
}

export function hasAudioConstraint(constraints?: MediaStreamConstraints) {
  return Boolean(constraints?.audio);
}

export function getVideoOnlyConstraints(constraints: MediaStreamConstraints) {
  return { ...constraints, audio: false };
}

export function getAudioContext() {
  const AudioContextConstructor = window.AudioContext;
  return new AudioContextConstructor();
}

export function createAgentAudioState(rawStream: MediaStream): AgentAudioState {
  const audioContext = getAudioContext();
  const sourceNode = audioContext.createMediaStreamSource(rawStream);
  const gainNode = audioContext.createGain();
  const destination = audioContext.createMediaStreamDestination();

  gainNode.gain.value = 1;
  sourceNode.connect(gainNode);
  gainNode.connect(destination);

  return {
    audioContext,
    destination,
    gainNode,
    rawStream,
    sourceNode,
    hasCapturedSdkStream: false,
    shouldInterceptNextAudioRequest: true,
  };
}

export function releaseAgentAudioState(audioState: AgentAudioState) {
  audioState.sourceNode.disconnect();
  audioState.gainNode.disconnect();
  audioState.rawStream.getTracks().forEach((track) => {
    track.stop();
  });
  audioState.destination.stream.getTracks().forEach((track) => {
    track.stop();
  });
  void audioState.audioContext
    .close()
    .catch((error) =>
      console.debug(`${OZONETEL_DEBUG_PREFIX} Failed to close agent audio context`, error),
    );
}

function getPropertyDescriptorFromChain(object: object, property: PropertyKey) {
  let currentObject: object | null = object;

  while (currentObject) {
    const descriptor = Object.getOwnPropertyDescriptor(currentObject, property);
    if (descriptor) return descriptor;

    currentObject = Object.getPrototypeOf(currentObject);
  }
}

export function installGetUserMediaWrapper({
  mediaDevices,
  wrappedGetUserMedia,
}: {
  mediaDevices: MediaDevices;
  wrappedGetUserMedia: NativeGetUserMedia;
}) {
  const nativeGetUserMedia = mediaDevices.getUserMedia;
  if (typeof nativeGetUserMedia !== 'function') return;

  const ownDescriptor = Object.getOwnPropertyDescriptor(mediaDevices, 'getUserMedia');
  const effectiveDescriptor =
    ownDescriptor ?? getPropertyDescriptorFromChain(mediaDevices, 'getUserMedia');
  const patchState: GetUserMediaPatchState = {
    hadOwnDescriptor: Boolean(ownDescriptor),
    mediaDevices,
    nativeGetUserMedia,
    ownDescriptor,
    wrappedGetUserMedia,
  };

  try {
    Object.defineProperty(mediaDevices, 'getUserMedia', {
      configurable: true,
      enumerable: effectiveDescriptor?.enumerable ?? false,
      value: wrappedGetUserMedia,
      writable: true,
    });
  } catch (error) {
    try {
      mediaDevices.getUserMedia = wrappedGetUserMedia;
    } catch {
      console.debug(`${OZONETEL_DEBUG_PREFIX} Failed to install microphone stream wrapper`, error);
      return;
    }
  }

  if (mediaDevices.getUserMedia !== wrappedGetUserMedia) {
    console.debug(`${OZONETEL_DEBUG_PREFIX} Microphone stream wrapper was not installed`);
    return;
  }

  return patchState;
}

export function restoreGetUserMediaWrapper(patchState: GetUserMediaPatchState) {
  const { hadOwnDescriptor, mediaDevices, nativeGetUserMedia, ownDescriptor, wrappedGetUserMedia } =
    patchState;

  if (mediaDevices.getUserMedia !== wrappedGetUserMedia) return;

  try {
    if (hadOwnDescriptor && ownDescriptor) {
      Object.defineProperty(mediaDevices, 'getUserMedia', ownDescriptor);
      return;
    }

    delete (mediaDevices as Partial<MediaDevices>).getUserMedia;
  } catch {
    mediaDevices.getUserMedia = nativeGetUserMedia;
  }
}

export function createOzonetelGetUserMediaWrapper({
  getAudioState,
  mediaDevices,
  nativeGetUserMedia,
  restoreGetUserMedia,
}: {
  getAudioState: () => AgentAudioState | undefined;
  mediaDevices: MediaDevices;
  nativeGetUserMedia: NativeGetUserMedia;
  restoreGetUserMedia: () => void;
}): NativeGetUserMedia {
  return async function getUserMedia(constraints) {
    const audioState = getAudioState();
    const shouldIntercept =
      audioState?.shouldInterceptNextAudioRequest && hasAudioConstraint(constraints);

    if (!shouldIntercept) {
      return nativeGetUserMedia.call(mediaDevices, constraints);
    }

    audioState.shouldInterceptNextAudioRequest = false;
    restoreGetUserMedia();

    const proxyAudioTrack = audioState.destination.stream.getAudioTracks()[0];
    if (!proxyAudioTrack) {
      console.debug(`${OZONETEL_DEBUG_PREFIX} Agent audio proxy track is not available`);
      return nativeGetUserMedia.call(mediaDevices, constraints);
    }

    const tracks: MediaStreamTrack[] = [proxyAudioTrack];
    if (constraints?.video) {
      const videoStream = await nativeGetUserMedia.call(
        mediaDevices,
        getVideoOnlyConstraints(constraints),
      );
      tracks.push(...videoStream.getVideoTracks());
    }

    audioState.hasCapturedSdkStream = true;
    return new MediaStream(tracks);
  };
}
